import { http } from "../../utils/config";

const staffAPI = {
    // Get all with pagination and search
    getStaffs: (params = {}) => {
        const {
            page = 1,
            per_page = 15,
            search = '',
            orgId = '',
            status = '',
            sort_by = 'created_at',
            sort_order = 'desc'
        } = params;

        const queryParams = new URLSearchParams({
            page: page.toString(),
            per_page: per_page.toString(),
            sort_by,
            sort_order,
            ...(search && { search }),
            ...(orgId && { orgId }),
            ...(status !== '' && { status })
        });

        return http.get(`/api/v1/staffs?${queryParams}`);
    },

    getStaffAll: (params = {}) => {
        return http.get(`/api/v1/staffs/all`, { params });
    },

    // Get by ID
    getStaffById: (id) => {
        return http.get(`/api/v1/staffs/${id}`);
    },

    // Create new
    createStaff: (staffData) => {
        return http.post('/api/v1/staffs', staffData);
    },

    // Update
    updateStaff: (id, staffData) => {
        return http.put(`/api/v1/staffs/${id}`, staffData);
    },

    // Delete
    deleteStaff: (id) => {
        return http.delete(`/api/v1/staffs/${id}`);
    },

    // Update Password
    updateStaffPassword: (id, password) => {
        return http.patch(`/api/v1/staffs/${id}/password`, { password });
    },


};

// Helper functions for handling API responses
export const staffHelpers = {
    // Extract data from API response
    extractStaffData: (response) => {
        if (response.data && response.data.success) {
            return response.data.data;
        }
        throw new Error(response.data?.message || 'Unknown error occurred');
    },

    // Extract paginated list from API response
    extractStaffList: (response) => {
        if (response.data && response.data.success) {
            const data = response.data.data;
            return {
                staffs: data.data || [],
                pagination: {
                    current: data.current_page,
                    pageSize: data.per_page,
                    total: data.total,
                    from: data.from,
                    to: data.to,
                    lastPage: data.last_page,
                    nextPageUrl: data.next_page_url,
                    prevPageUrl: data.prev_page_url
                }
            };
        }
        throw new Error(response.data?.message || 'Unknown error occurred');
    },

    // Format data for form submission
    formatStaffData: (formData) => {
        return {
            name: formData.name?.trim(),
            email: formData.email?.trim().toLowerCase(),
            phone: formData.phone?.trim(),
            dob: formData.dob,
            address: formData.address?.trim(),
            description: formData.description || null,
            status: formData.status ?? true,
            // Một nhân sự phụ trách được nhiều đơn vị. `organization_id` giữ lại
            // cho màn cũ còn gửi đơn trị; backend ưu tiên `organization_ids`.
            organization_id: formData.organization_id || null,
            organization_ids: Array.isArray(formData.organization_ids)
                ? formData.organization_ids
                : (formData.organization_id ? [formData.organization_id] : []),
            account_type: formData.account_type ?? 'STAFF',
            password: formData.password || null
        };
    },

    // Validate data before submission
    validateStaffData: (staffData) => {
        const errors = {};

        if (!staffData.name?.trim()) {
            errors.name = 'Tên khách hàng không được để trống';
        }

        if (staffData.email && !/\S+@\S+\.\S+/.test(staffData.email)) {
            errors.email = 'Email không hợp lệ';
        }

        if (!staffData.phone?.trim()) {
            errors.phone = 'Số điện thoại không được để trống';
        } else if (!/^[0-9]{10,11}$/.test(staffData.phone.replace(/\s/g, ''))) {
            errors.phone = 'Số điện thoại không hợp lệ';
        }

        if (staffData.dob) {
            const birthDate = new Date(staffData.dob);
            const today = new Date();
            if (birthDate > today) {
                errors.dob = 'Ngày sinh không thể lớn hơn ngày hiện tại';
            }
        }

        if (!['BRANCH_MANAGER', 'STAFF', 'WARD_LEADER'].includes(staffData.account_type)) {
            errors.account_type = 'Loại tài khoản không hợp lệ';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
    return async (...args) => {
        try {
            const response = await apiFunction(...args);
            return response;
        } catch (error) {
            console.error('API Error:', error);

            // Handle different types of errors
            if (error.response) {
                // Server responded with error status
                const { status, data } = error.response;
                switch (status) {
                    case 400:
                        throw new Error(data.message || 'Dữ liệu không hợp lệ');
                    case 401:
                        throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
                    case 403:
                        throw new Error('Bạn không có quyền thực hiện thao tác này');
                    case 404:
                        throw new Error('Không tìm thấy khách hàng');
                    case 422: {
                        // StoreStaffRequest/UpdateStaffRequest trả {success:false, errors:{field:[...]}}
                        // và KHÔNG có `message`, nên chỉ đọc data.message thì lỗi theo trường bị
                        // nuốt mất, người dùng chỉ thấy câu chung chung. Gắn kèm để form hiện đúng ô.
                        const validationError = new Error(data.message || 'Dữ liệu không hợp lệ');
                        validationError.fieldErrors = data.errors || null;
                        throw validationError;
                    }
                    case 500:
                        throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
                    default:
                        throw new Error(data.message || 'Đã có lỗi xảy ra');
                }
            } else if (error.request) {
                // Network error
                throw new Error('Không thể kết nối đến server');
            } else {
                // Other error
                throw new Error(error.message || 'Đã có lỗi xảy ra');
            }
        }
    };
};

// Wrapped API functions with error handling
export const staffAPIWithErrorHandling = {
    getStaffs: withErrorHandling(staffAPI.getStaffs),
    getStaffAll: withErrorHandling(staffAPI.getStaffAll),
    getStaffById: withErrorHandling(staffAPI.getStaffById),
    createStaff: withErrorHandling(staffAPI.createStaff),
    updateStaff: withErrorHandling(staffAPI.updateStaff),
    deleteStaff: withErrorHandling(staffAPI.deleteStaff),
    updateStaffPassword: withErrorHandling(staffAPI.updateStaffPassword),
};

export default staffAPI;